import React from "react";
import {
  alpha,
  AppBar,
  Avatar,
  Box,
  Fab,
  InputBase,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import theme from "../../../theme";
import { Link, useLocation } from "react-router-dom";
import Drawer from "../../base/Drawer";
import MenuView from "./Menu";
import { Apps, Clear, Edit, NotificationsActive } from "@mui/icons-material";
import Clock from "react-live-clock";
import { useDispatch, useSelector } from "../../../store";
import { toggleEditMode, toggleMenu } from "../../../slices/shared";
import SearchIcon from "@mui/icons-material/Search";
import AlertMenu from "./AlertMenu";
import PulsingBadge from "../../base/PulsingBadge";
import ProfileMenuView from "./ProfileMenu";
import checkRole, { UserRoles } from "../../../helpers/checkRole";
import CompanyLogo from "../../base/CompanyLogo";

interface Props {}

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 25,
  backgroundColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.8),
  },
  marginRight: 30,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    fontWeight: 600,
    fontSize: "20px",
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingTop: "8px",
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
    },
  },
}));

const Navbar: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { menuOpen, editMode, productLicensed } = useSelector(
    (store) => store.shared,
  );
  const { loggedInUser, userCompany } = useSelector((store) => store.auth);
  const { settings } = useSelector((store) => store.settings);
  const { siteAlerts } = useSelector((store) => store.alert);

  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar
        sx={{
          height: "65px",
          backgroundColor: theme.palette.background.paper,
          justifyContent: "space-between",
          paddingRight: 2,
        }}
        disableGutters
      >
        <Box
          display="flex"
          flexDirection={"row"}
          gap={1}
          alignContent="center"
          alignItems="center"
          height="100%"
          width="100%"
        >
          <Link
            style={{
              height: "100%",
              objectFit: "cover",
            }}
            to={`/dashboard`}
          >
            {userCompany?.logo ? (
              <Box py={"5px"} pl={2}>
                <CompanyLogo company={userCompany} size="55px" />
              </Box>
            ) : (
              <img
                height={xs ? "60px" : "100%"}
                width={xs ? "100%" : 200}
                src={"/static/images/SVPro.png"}
                alt="logo"
                style={{
                  objectFit: "cover",
                }}
              />
            )}
          </Link>
          <Typography
            display={{ xs: "none", md: "flex" }}
            color="textPrimary"
            variant={"h3"}
            fontWeight="600"
          >
            <Clock
              ticking={true}
              format={"hh:mm:ss A"}
              style={{
                display: "flex",
              }}
            />
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection={"row"}
          gap={1}
          alignItems="center"
          justifyContent="flex-end"
          height="100%"
          width="100%"
        >
          <Box display={{ xs: "none", md: "flex" }} visibility="hidden">
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search"
                inputProps={{ "aria-label": "search" }}
              />
            </Search>
          </Box>
          {location.pathname.startsWith("/dashboard") &&
            checkRole(loggedInUser!, UserRoles.AnyAdmin) && (
              <Box>
                <Fab
                  disabled={!productLicensed}
                  color={editMode ? "info" : "primary"}
                  aria-label="edit"
                  size={xs ? "small" : "medium"}
                  onClick={() => dispatch(toggleEditMode(!editMode))}
                >
                  {editMode ? <Clear /> : <Edit />}
                </Fab>
              </Box>
            )}
          {settings?.notificationSettings?.siteNotifications === true &&
            (siteAlerts?.filter((a) => !a.read).length! > 0 ? (
              <PulsingBadge
                color={theme.palette.error.main}
                content={
                  <Fab
                    disabled={!productLicensed}
                    color={menuOpen === "alerts" ? "info" : "primary"}
                    aria-label="alerts"
                    size={xs ? "small" : "medium"}
                    onClick={() =>
                      dispatch(
                        toggleMenu(menuOpen !== "alerts" ? "alerts" : "none"),
                      )
                    }
                  >
                    <NotificationsActive />
                  </Fab>
                }
              />
            ) : (
              <Box>
                <Fab
                  disabled={!productLicensed}
                  color={menuOpen === "alerts" ? "info" : "primary"}
                  aria-label="alerts"
                  size={xs ? "small" : "medium"}
                  onClick={() =>
                    dispatch(
                      toggleMenu(menuOpen !== "alerts" ? "alerts" : "none"),
                    )
                  }
                >
                  <NotificationsActive />
                </Fab>
              </Box>
            ))}
          <Box>
            <Fab
              disabled={!productLicensed}
              color={menuOpen === "menu" ? "info" : "primary"}
              aria-label="menu"
              size={xs ? "small" : "medium"}
              onClick={() =>
                dispatch(toggleMenu(menuOpen !== "menu" ? "menu" : "none"))
              }
            >
              <Apps />
            </Fab>
          </Box>
          <Box>
            <Fab
              color={menuOpen === "profile" ? "info" : "primary"}
              aria-label="profile"
              size={xs ? "small" : "medium"}
              onClick={() =>
                dispatch(
                  toggleMenu(menuOpen !== "profile" ? "profile" : "none"),
                )
              }
            >
              <Avatar
                sx={{
                  backgroundColor: "transparent",
                  color: "#fff",
                  height: "50px",
                  width: "50px",
                  ":hover": { cursor: "pointer" },
                }}
              >
                {loggedInUser?.firstName?.[0] || ""}
                {loggedInUser?.lastName?.[0] || ""}
              </Avatar>
            </Fab>
          </Box>
        </Box>
        <Drawer
          open={menuOpen === "menu"}
          handleOpen={() => dispatch(toggleMenu("menu"))}
          handleClose={() => dispatch(toggleMenu("none"))}
          contents={
            <MenuView handleClose={() => dispatch(toggleMenu("none"))} />
          }
        />
        <Drawer
          open={menuOpen === "alerts"}
          handleOpen={() => dispatch(toggleMenu("alerts"))}
          handleClose={() => dispatch(toggleMenu("none"))}
          contents={
            <AlertMenu handleClose={() => dispatch(toggleMenu("none"))} />
          }
        />
        <Drawer
          open={menuOpen === "profile"}
          handleOpen={() => dispatch(toggleMenu("profile"))}
          handleClose={() => dispatch(toggleMenu("none"))}
          contents={
            <ProfileMenuView handleClose={() => dispatch(toggleMenu("none"))} />
          }
        />
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
